import * as React from "react";

import { useControlledContextualMenuByMenuButton } from "../../AppFlyoutAnchor/next";
import type {
  AppSplitButtonProps,
  AppSplitButtonState,
} from "./AppSplitButton.types";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";

export const useAppSplitButtonState = (
  props: AppSplitButtonProps,
  state: AppSplitButtonState
): AppSplitButtonState => {
  const context = React.useContext(AppContext);
  const isHideMenuOnPrimaryButtonClickFixEnabled = !!checkExperiment(
    context,
    ribbonExperiments.hideMenuOnPrimaryButtonClickFixEnabled
  );

  const { onClick, disabled } = state.primaryActionButton;
  const [hideMenuFromPrimaryButton, setHideMenuFromPrimaryButton] =
    React.useState<boolean>(false);

  useControlledContextualMenuByMenuButton(
    { ...props, onExecute: props.onMenuButtonExecute },
    state,
    state.root.ref
  );

  const isDisabledControlsChangeGateEnabled = !!checkExperiment(
    context,
    ribbonExperiments.isDisabledControlsChangeGateEnabled
  );

  // Adding onClick to the primary button to close the menu if it's open
  const onButtonClick = React.useCallback(
    (
      ev: React.MouseEvent<HTMLButtonElement> &
        React.MouseEvent<HTMLAnchorElement>
    ) => {
      if (isDisabledControlsChangeGateEnabled && disabled) {
        return;
      }
      onClick?.(ev);

      if (!state.menu.hidden && isHideMenuOnPrimaryButtonClickFixEnabled) {
        setHideMenuFromPrimaryButton(true);
        // Set the menu button aria expanded to false and calling the menu onDismiss
        state.menuButton["aria-expanded"] = false;
        state.menu.onDismiss?.();
      }
    },
    [
      isHideMenuOnPrimaryButtonClickFixEnabled,
      onClick,
      state.menu,
      state.menuButton,
      disabled,
      isDisabledControlsChangeGateEnabled,
    ]
  );

  state.primaryActionButton.onClick = onButtonClick;

  // Once the menu has been hidden, reset the state so the menu can be opened again from the menu button
  if (hideMenuFromPrimaryButton && isHideMenuOnPrimaryButtonClickFixEnabled) {
    state.menu.hidden = true;
    setHideMenuFromPrimaryButton(false);
  }

  // Create menuHidden prop to pass to MenuButton
  // This is for styling, to know if the MenuButton should style in the "expanded" state
  state.menuButton.menuHidden = state.menu.hidden;
  state.menu.useLegacyContextMenu = props.useLegacyContextMenu;

  return state;
};
